import { alpha, type Theme, type Components } from "@mui/material/styles";

export const inputsCustomizations: Components<Theme> = {
  MuiButtonBase: {
    defaultProps: { disableRipple: true },
    styleOverrides: {
      root: ({ theme }) => ({
        transition:
          "background-color 160ms ease-out, border-color 160ms ease-out, color 160ms ease-out, opacity 160ms ease-out",
        "&:focus-visible, &.Mui-focusVisible": {
          outline: `3px solid ${theme.palette.primary.main}`,
          outlineOffset: 3,
        },
        "@media (prefers-reduced-motion: reduce)": { transition: "none" },
      }),
    },
  },
  MuiButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        minHeight: 48,
        padding: "10px 20px",
        borderRadius: 4,
        boxShadow: "none",
        textTransform: "none",
        fontWeight: 500,
        fontFamily: theme.typography.fontFamily,
        "&:hover": { boxShadow: "none" },
        "&:active:not(.Mui-disabled)": { opacity: 0.88 },
      }),
      sizeSmall: { minHeight: 40, padding: "6px 12px" },
      sizeLarge: { minHeight: 52, padding: "12px 24px" },
      containedPrimary: ({ theme }) => ({
        color: theme.palette.background.default,
        backgroundColor: theme.palette.text.primary,
        "&:hover": {
          backgroundColor:
            theme.palette.mode === "dark" ? "#d2d3d0" : "#3a3e4c",
        },
        "&.Mui-disabled": {
          color: theme.palette.action.disabled,
          backgroundColor: theme.palette.action.disabledBackground,
        },
      }),
      outlinedPrimary: ({ theme }) => ({
        color: theme.palette.text.primary,
        borderColor: theme.palette.divider,
        "&:hover": {
          borderColor: theme.palette.text.secondary,
          backgroundColor: theme.palette.action.hover,
        },
      }),
      textPrimary: ({ theme }) => ({ color: theme.palette.text.primary }),
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: ({ theme, ownerState }) => ({
        width: 44,
        height: 44,
        borderRadius: 4,
        ...(ownerState.color === "default" && {
          color: theme.palette.text.secondary,
        }),
        "&:hover": { backgroundColor: theme.palette.action.hover },
        "&:active": { backgroundColor: theme.palette.action.selected },
      }),
      sizeSmall: { width: 40, height: 40 },
    },
  },
  MuiToggleButtonGroup: { styleOverrides: { root: { borderRadius: 4 } } },
  MuiToggleButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        minHeight: 40,
        padding: "6px 14px",
        textTransform: "none",
        fontWeight: 500,
        color: theme.palette.text.secondary,
        borderColor: theme.palette.divider,
        "&.Mui-selected": {
          color: theme.palette.primary.main,
          backgroundColor: theme.palette.action.selected,
          "&:hover": {
            backgroundColor: alpha(theme.palette.primary.main, 0.18),
          },
        },
      }),
    },
  },
  MuiCheckbox: { styleOverrides: { root: { padding: 9 } } },
  MuiRadio: { styleOverrides: { root: { padding: 9 } } },
  MuiInputBase: {
    styleOverrides: {
      input: ({ theme }) => ({
        fontSize: "1rem",
        "&::placeholder": { opacity: 1, color: theme.palette.text.secondary },
      }),
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: ({ theme }) => ({
        minHeight: 48,
        borderRadius: 4,
        backgroundColor: theme.palette.background.paper,
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.divider,
          transition: "border-color 160ms ease-out",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.text.secondary,
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.primary.main,
          borderWidth: 2,
        },
        "&.Mui-error .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.error.main,
        },
        "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.action.disabledBackground,
        },
        "&.MuiInputBase-sizeSmall": { minHeight: 40 },
        "&.MuiInputBase-multiline": {
          padding: "12px 14px",
          alignItems: "flex-start",
        },
        "& textarea": { lineHeight: 1.6 },
      }),
      input: {
        padding: "12px 14px",
        "&.MuiInputBase-inputSizeSmall": { padding: "8px 12px" },
        "&.MuiInputBase-inputMultiline": { padding: 0 },
      },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: {
        "&.MuiInputLabel-outlined:not(.MuiInputLabel-shrink)": {
          transform: "translate(14px, 12px) scale(1)",
        },
        "&.MuiInputLabel-outlined.MuiInputLabel-sizeSmall:not(.MuiInputLabel-shrink)":
          { transform: "translate(12px, 8px) scale(1)" },
      },
    },
  },
  MuiFormLabel: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: theme.palette.text.primary,
        fontSize: "0.875rem",
        marginBottom: 8,
      }),
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: { marginLeft: 0, fontSize: "0.8125rem", lineHeight: 1.5 },
    },
  },
};
